import { useState, useMemo, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, X, Check, Trash2, Files, AlertTriangle, Send, Undo2 } from "lucide-react";
import { S, MANROPE, FOCUS, API, trackedUrl, fullDesignPrompt, productOf, auDate, parseAuDate } from "./lib";
import SocialAssets from "./SocialAssets";
import SocialFlags from "./SocialFlags";
import SocialGates from "./SocialGates";
import SocialHistory from "./SocialHistory";

const copyOut = (text, what) => {
  if (!text) { toast.info(`No ${what} to copy yet`); return; }
  const done = navigator.clipboard?.writeText(text);
  if (!done) { toast.error("This browser won't let us reach the clipboard — copy the text by hand"); return; }
  done.then(() => toast.success(`${what} copied — paste it straight in`))
    .catch(() => toast.error("The browser blocked the clipboard — select the text and copy by hand"));
};

const Label = ({ children }) => (
  <div className="text-[10px] font-bold uppercase tracking-[0.09em] mb-1" style={{ color: S.ink3 }}>{children}</div>
);

/* Dates are typed and read the Australian way (07-Oct-2026); the ISO value the
   API stores never reaches the screen. */
const DateField = ({ label, value, onChange, testid }) => {
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(false);
  const commit = () => {
    setEditing(false);
    const raw = text.trim();
    if (!raw) { onChange(null); return; }
    const iso = parseAuDate(raw);
    if (!iso) { toast.error(`"${raw}" isn't a date — try 07-Oct-2026`); setText(""); return; }
    onChange(iso);
  };
  return (
    <div>
      <Label>{label}</Label>
      <input value={editing ? text : auDate(value, "")} data-testid={testid}
        placeholder="dd-MMM-yyyy"
        onFocus={() => { setText(auDate(value, "")); setEditing(true); }}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        className={`w-full h-9 rounded-[7px] px-2.5 text-[13px] bg-white ${FOCUS}`}
        style={{ border: `1px solid ${S.border}`, color: S.ink }} />
    </div>
  );
};

export default function SocialPostPanel({ post, settings, statuses, statusLabels, staff = [], onClose, onSaved }) {
  const [f, setF] = useState({ ...post });
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const isNew = !post.id;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (k) => () => setF({ ...f, [k]: !f[k] });

  // The post arrives from the calendar, which may be a moment stale.
  useEffect(() => { setF({ ...post }); }, [post]);

  const flags = f.flags || [];
  const blocks = flags.filter((x) => x.severity === "block");

  const liLink = useMemo(() => trackedUrl(f, settings, "linkedin"), [f, settings]);
  const metaLink = useMemo(() => trackedUrl(f, settings, "meta"), [f, settings]);
  const prompt = useMemo(() => fullDesignPrompt(f, settings), [f, settings]);
  const product = productOf(settings, f.product);
  const today = new Date().toISOString().slice(0, 10);
  const overdue = f.post_date && f.post_date < today && f.status !== "published";
  const consentGate = !!(f.consent_note || "").trim() && !f.consent_cleared;

  const field = (k, label, rows) => (
    <div>
      <Label>{label}</Label>
      {rows ? (
        <textarea value={f[k] || ""} onChange={set(k)} rows={rows} data-testid={`social-field-${k}`}
          className={`w-full rounded-[7px] px-2.5 py-2 text-[13px] bg-white ${FOCUS}`}
          style={{ border: `1px solid ${S.border}`, color: S.ink, lineHeight: 1.5 }} />
      ) : (
        <input value={f[k] || ""} onChange={set(k)} data-testid={`social-field-${k}`}
          className={`w-full h-9 rounded-[7px] px-2.5 text-[13px] bg-white ${FOCUS}`}
          style={{ border: `1px solid ${S.border}`, color: S.ink }} />
      )}
    </div>
  );

  const pick = (k, label, options) => (
    <div>
      <Label>{label}</Label>
      <select value={f[k] || ""} onChange={set(k)} data-testid={`social-field-${k}`}
        className={`w-full h-9 rounded-[7px] px-2 text-[13px] bg-white ${FOCUS}`}
        style={{ border: `1px solid ${S.border}`, color: S.ink }}>
        <option value="">Not set</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f };
      ["product_label", "product_colour", "tracked_url", "tracked_url_meta", "full_design_prompt",
        "overdue", "consent_gate", "draft", "created_at", "updated_at", "id",
        "assets", "asset_count", "has_image", "has_video", "gates", "flags", "blocking_flags",
        "warning_flags", "open_gates", "awaiting_approval", "flags_checked_at", "version",
        "last_actor", "last_actor_kind", "created_by", "sent_back_by", "sent_back_at",
        "reopened_note", "reopened_by", "reopened_at", "reopened_from",
        "approved_by", "approved_at", "published_at", "published_by"].forEach((k) => delete body[k]);
      if (isNew) {
        const r = await axios.post(`${API}/posts`, body);
        toast.success("Post added");
        onSaved(r.data.post);
      } else {
        const r = await axios.put(`${API}/posts/${post.id}`, { ...body, expected_version: f.version });
        toast.success("Post saved");
        onSaved(r.data.post);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that post"); }
    finally { setBusy(false); }
  };

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      await axios.post(`${API}/posts/${post.id}${path}`, body || {});
      toast.success(msg); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };

  const recheck = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API}/posts/${post.id}/check`);
      setF({ ...f, flags: r.data.flags });
      toast.success(r.data.blocking.length
        ? `${r.data.blocking.length} thing(s) to fix before this can be approved`
        : "Nothing breaches the house rules");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run the rules"); }
    finally { setChecking(false); }
  };

  const sendBack = async () => {
    const note = window.prompt("What needs doing before this can go out?");
    if (!note || !note.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/posts/${post.id}/send-back`, { note: note.trim() });
      toast.success("Sent back to draft with your note"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send it back"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await axios.delete(`${API}/posts/${post.id}`); toast.success("Post deleted"); onSaved(); }
    catch { toast.error("Could not delete that post"); setBusy(false); }
  };

  const CopyBtn = ({ text, what, testid }) => (
    <button onClick={() => copyOut(text, what)} data-testid={testid}
      className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase ${FOCUS}`}
      style={{ color: S.accentInk, letterSpacing: "0.04em" }}>
      <Copy className="w-3 h-3" /> Copy
    </button>
  );

  const LinkRow = ({ label, url, what, testid }) => (
    <div className="rounded-[7px] px-2.5 py-2 text-[11.5px] break-all" style={{ background: S.panel, color: S.ink2 }}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <Label>{label}</Label>
        <CopyBtn text={url} what={what} testid={`${testid}-copy`} />
      </div>
      <span className="mono" data-testid={testid}>{url}</span>
    </div>
  );

  const readiness = [["copy_done", "Copy done"], ["visual_done", "Visual done"], ["reel_done", "Reel done"]];

  return (
    <div className="fixed inset-0 z-[90] flex justify-end" data-testid="social-post-panel">
      <div className="absolute inset-0" style={{ background: "rgba(18,18,18,0.32)" }} onClick={onClose} />
      <div className="relative h-full w-full max-w-[940px] overflow-y-auto"
        style={{ background: S.panel, borderLeft: `1px solid ${S.border}` }}>
        <div className="sticky top-0 z-10 px-5 py-3.5 flex items-center gap-3 flex-wrap"
          style={{ background: S.card, borderBottom: `1px solid ${S.border}` }}>
          <div className="min-w-0">
            <div style={{ ...MANROPE, fontSize: 16, fontWeight: 800, color: S.ink }}>
              {isNew ? "New post" : f.post_id}
            </div>
            <div className="text-[11.5px]" style={{ color: S.ink2 }}>
              {product.label || "No product"} · {f.vertical || "No vertical"}
              {f.post_date ? ` · ${auDate(f.post_date)}` : " · no date yet"}
            </div>
          </div>
          <select value={f.status || "draft"} onChange={set("status")} data-testid="social-status"
            className={`h-9 rounded-[7px] px-2 text-[12.5px] bg-white ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, color: S.ink }}>
            {statuses.map((s) => <option key={s} value={s}>{statusLabels[s] || s}</option>)}
          </select>
          {!isNew && f.status !== "published" && (
            <button onClick={() => act("/submit", { expected_version: f.version },
              "Submitted for approval")} disabled={busy} data-testid="social-submit"
              className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
              style={{ background: S.card, color: blocks.length ? S.muted : S.ink2,
                border: `1px solid ${S.border}` }}
              title={blocks.length ? "Fix the house-rule breaches first" : "Put this in front of the approver"}>
              <Send className="w-3.5 h-3.5 inline mr-1" />Submit
            </button>
          )}
          {!isNew && f.status === "awaiting_approval" && (
            <button onClick={sendBack} disabled={busy} data-testid="social-send-back"
              className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
              style={{ background: S.card, color: S.bad, border: "1px solid #E8C4C4" }}>
              <Undo2 className="w-3.5 h-3.5 inline mr-1" />Send back
            </button>
          )}
          {!isNew && (
            <button onClick={() => act("/approve", null, f.approved ? "Approval removed" : "Approved")}
              data-testid="social-approve"
              className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
              style={{ background: f.approved ? S.good : S.card, color: f.approved ? "#fff" : S.ink2,
                border: `1px solid ${f.approved ? S.good : S.border}` }}>
              <Check className="w-3.5 h-3.5 inline mr-1" />{f.approved ? "Approved" : "Approve"}
            </button>
          )}
          <button onClick={save} disabled={busy} data-testid="social-save"
            className={`ml-auto h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase disabled:opacity-60 ${FOCUS}`}
            style={{ background: S.accent, border: `1px solid ${S.accentEdge}`, color: "#000" }}>
            {busy ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} data-testid="social-close" className={`w-9 h-9 rounded-[7px] flex items-center justify-center ${FOCUS}`}
            style={{ border: `1px solid ${S.border}`, background: S.card }}><X className="w-4 h-4" /></button>
        </div>

        {(consentGate || overdue || f.sent_back_note || f.reopened_note) && (
          <div className="px-5 pt-4 space-y-2" data-testid="social-warnings">
            {f.reopened_note && f.status === "awaiting_approval" && (
              <div className="rounded-[8px] px-3 py-2.5 flex items-start gap-2 text-[12.5px]"
                style={{ background: "#FFF8D6", border: "1px solid #E8C86A", color: "#6B5600" }}
                data-testid="social-reopened-note">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{f.reopened_note}</span>
              </div>
            )}
            {f.sent_back_note && (
              <div className="rounded-[8px] px-3 py-2.5 flex items-start gap-2 text-[12.5px]"
                style={{ background: "#FFF1EC", border: "1px solid #F0C4B4", color: "#8E3A12" }}
                data-testid="social-sent-back-note">
                <Undo2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span><b>Sent back{f.sent_back_by ? ` by ${f.sent_back_by}` : ""}:</b> {f.sent_back_note}</span>
              </div>
            )}
            {consentGate && (
              <div className="rounded-[8px] px-3 py-2.5 flex items-start gap-2 text-[12.5px]"
                style={{ background: "#FFF1EC", border: "1px solid #F0C4B4", color: "#8E3A12" }}
                data-testid="social-warning-consent">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span><b>Hold — consent not cleared.</b> {f.consent_note}</span>
              </div>
            )}
            {overdue && (
              <div className="rounded-[8px] px-3 py-2.5 flex items-start gap-2 text-[12.5px]"
                style={{ background: "#FFF8D6", border: "1px solid #E8D48A", color: S.accentInk }}
                data-testid="social-warning-overdue">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>This slot was {auDate(f.post_date)} and still isn't published — move the date or mark it published.</span>
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-4 grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] items-start">
          <div className="space-y-3.5">
            {!isNew && <SocialFlags flags={flags} checkedAt={f.flags_checked_at}
              onRecheck={recheck} checking={checking} />}
            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="flex items-center justify-between">
                <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>LinkedIn brand page</div>
                <CopyBtn text={f.linkedin_copy} what="LinkedIn copy" testid="social-copy-linkedin" />
              </div>
              {field("hook", "Hook (first line)", 2)}
              {field("linkedin_copy", "Post copy", 10)}
              {pick("linkedin_account", "Posting account", (settings.linkedin_accounts || []).map((v) => [v, v]))}
              <label className="flex items-center gap-2 text-[12.5px]" style={{ color: S.ink2 }}>
                <input type="checkbox" checked={!!f.anthony_reposts} onChange={toggle("anthony_reposts")}
                  data-testid="social-field-anthony_reposts" />
                Anthony reposts this one
              </label>
              <label className="flex items-center gap-2 text-[12.5px]" style={{ color: S.ink2 }}>
                <input type="checkbox" checked={!!f.unbranded} onChange={toggle("unbranded")}
                  data-testid="social-field-unbranded" />
                Unbranded — skip the #EQUIPR hashtag rule (teasers, the build story)
              </label>
              <label className="flex items-center gap-2 text-[12.5px]" style={{ color: S.ink2 }}>
                <input type="checkbox" checked={!!f.cta_exempt} onChange={toggle("cta_exempt")}
                  data-testid="social-field-cta_exempt" />
                Asks for a conversation, not a click — skip the call-to-action rule
              </label>
              {f.anthony_reposts && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Share commentary</Label>
                    <CopyBtn text={f.repost_commentary} what="Repost commentary" testid="social-copy-repost" />
                  </div>
                  <textarea value={f.repost_commentary || ""} onChange={set("repost_commentary")} rows={4}
                    data-testid="social-field-repost_commentary"
                    className={`w-full rounded-[7px] px-2.5 py-2 text-[13px] bg-white ${FOCUS}`}
                    style={{ border: `1px solid ${S.border}`, color: S.ink }} />
                </div>
              )}
            </div>

            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="flex items-center justify-between">
                <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>
                  Facebook reel <span className="text-[11.5px] font-normal" style={{ color: S.ink3 }}>· auto-posts to Instagram</span>
                </div>
                <CopyBtn text={f.reel_caption} what="Reel caption" testid="social-copy-reel" />
              </div>
              {field("reel_caption", "Caption", 5)}
              {field("reel_concept", "Reel concept", 3)}
            </div>

            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="flex items-center justify-between">
                <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>Visual brief</div>
                <CopyBtn text={prompt} what="Design prompt" testid="social-copy-prompt" />
              </div>
              {field("visual_headline", "Visual headline")}
              {field("design_prompt", "Design prompt block", 6)}
              <p className="text-[11.5px]" style={{ color: S.ink3 }}>
                Copy sends the base template{f.stream && (settings.stream_templates || {})[f.stream]
                  ? ` for ${f.stream}` : product.label ? ` for ${product.label}` : ""} with this block appended
                {prompt ? ` — ${prompt.length.toLocaleString()} characters.` : "."}
              </p>
            </div>

            {!isNew && <SocialAssets post={f} onChanged={(updated) => {
              if (updated) setF({ ...f, assets: updated.assets, visual_done: updated.visual_done,
                reel_done: updated.reel_done });
            }} />}
          </div>

          <div className="space-y-3.5">
            {!isNew && <SocialGates post={f} onChanged={(updated) => updated && setF({ ...f, ...updated })} />}
            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div className="grid grid-cols-2 gap-3">
                <DateField label="Date" value={f.post_date} testid="social-field-post_date"
                  onChange={(iso) => setF({ ...f, post_date: iso })} />
                {field("post_time", "Time (optional)")}
                {pick("product", "Product", (settings.products || []).map((p) => [p.key, p.label]))}
                {pick("vertical", "Vertical", (settings.verticals || []).map((v) => [v, v]))}
                {pick("stream", "Stream", (settings.streams || []).map((v) => [v, v]))}
                {field("week_label", "Week")}
                {field("topic", "Feature / topic")}
                {field("campaign", "Campaign")}
              </div>
              <div>
                <Label>Owner — who gets the nudge</Label>
                <select value={f.owner_id || ""} data-testid="social-field-owner"
                  onChange={(e) => {
                    const s = staff.find((x) => x.id === e.target.value);
                    setF({ ...f, owner_id: s ? s.id : null, owner_name: s ? s.name : null });
                  }}
                  className={`w-full h-9 rounded-[7px] px-2 text-[13px] bg-white ${FOCUS}`}
                  style={{ border: `1px solid ${S.border}`, color: S.ink }}>
                  <option value="">
                    {settings.default_owner_name ? `${settings.default_owner_name} (default)` : "Nobody yet"}
                  </option>
                  {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 flex-wrap pt-1">
                {readiness.map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5 text-[12.5px]" style={{ color: S.ink2 }}>
                    <input type="checkbox" checked={!!f[k]} onChange={toggle(k)} data-testid={`social-field-${k}`} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>Link and tracking</div>
              {field("destination_url", "Destination link")}
              {field("utm_campaign", "UTM campaign")}
              {pick("link_placement", "Where the link goes", (settings.link_placement_options || []).map((v) => [v, v]))}
              {liLink
                ? <>
                    <LinkRow label="LinkedIn link" url={liLink} what="LinkedIn tracked URL" testid="social-tracked-url" />
                    <LinkRow label="Facebook / Instagram link" url={metaLink} what="Meta tracked URL" testid="social-tracked-url-meta" />
                  </>
                : <p className="text-[11.5px]" style={{ color: S.ink3 }}>
                    Add a destination link, or set the product's landing page in settings, and the tracked
                    links build themselves.
                  </p>}
            </div>

            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>Consent and QA</div>
              {field("consent_note", "Consent / approval gate", 3)}
              <label className="flex items-center gap-2 text-[12.5px]" style={{ color: S.ink2 }}>
                <input type="checkbox" checked={!!f.consent_cleared} onChange={toggle("consent_cleared")}
                  data-testid="social-field-consent_cleared" />
                Consent cleared
              </label>
              {field("qa_flags", "QA flags", 2)}
              {field("notes", "Notes", 2)}
              {(f.imported_status_note || f.source_note) && (
                <p className="text-[11.5px]" style={{ color: S.ink3 }} data-testid="social-import-note">
                  {f.imported_status_note ? `From the spreadsheet: ${f.imported_status_note}. ` : ""}
                  {f.source_note || ""}
                </p>
              )}
            </div>

            <div className="rounded-[10px] p-3.5 space-y-3" style={{ background: S.card, border: `1px solid ${S.border}` }}>
              <div style={{ ...MANROPE, fontSize: 13.5, fontWeight: 700, color: S.ink }}>Once it's live</div>
              {field("live_linkedin_url", "LinkedIn URL")}
              {field("live_meta_url", "Facebook / Instagram URL")}
              {!isNew && (
                <button onClick={() => act("/publish", { live_linkedin_url: f.live_linkedin_url,
                  live_meta_url: f.live_meta_url }, "Marked published")}
                  data-testid="social-publish"
                  className={`w-full h-9 rounded-[7px] text-[12px] font-bold uppercase ${FOCUS}`}
                  style={{ background: S.good, color: "#fff" }}>
                  Mark published
                </button>
              )}
            </div>

            {!isNew && <SocialHistory postUid={post.id} />}

            {!isNew && (
              <div className="flex items-center gap-2">
                <button onClick={() => act("/duplicate", null, "Post duplicated")} data-testid="social-duplicate"
                  className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ${FOCUS}`}
                  style={{ background: S.card, border: `1px solid ${S.border}`, color: S.ink2 }}>
                  <Files className="w-3.5 h-3.5 inline mr-1" /> Duplicate
                </button>
                <button onClick={remove} data-testid="social-delete"
                  className={`h-9 px-3 rounded-[7px] text-[11.5px] font-bold uppercase ml-auto ${FOCUS}`}
                  style={{ background: S.card, border: "1px solid #E8C4C4", color: "#8E1212" }}>
                  <Trash2 className="w-3.5 h-3.5 inline mr-1" /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
